import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import StudentEnrollmentModal from './parts/StudentEnrollmentModal';
import ResetPasswordModal from '../Authorisation/ResetPasswordModal';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';

interface ProfilePicture {
  file_name: string; key: string; cdn_url: string; dev_url: string;
  size: number; mime_type: string; etag: string;
  uploaded_at: { $date: { $numberLong: string } };
}

interface Enrollment {
  enrollment_id: string; enrollment_no: string | null;
  course_id: string | null; course_name: string | null; course_title: string | null;
  batch_id: string | null; batch_name: string | null; batch_time: string | null;
  start_month: string | null; start_year: number | null;
  end_month: string | null; end_year: number | null;
  date_of_admission: string | null; created_at: string | null;
}

interface Student {
  student_id: string; name: string; student_custom_id: string;
  dateOfBirth: string | null; gender: string | null; aadharNumber: string | null;
  category: string | null; mobileNumber: string | null; district: string | null;
  state: string | null; pinCode: string | null; bloodGroup: string | null;
  whatsappNumber: string | null; email: string | null;
  fatherMobile: string | null; fatherWhatsapp: string | null;
  motherMobile: string | null; motherWhatsapp: string | null;
  previousClass: string | null; yearOfPassing: string | null;
  schoolBoard: string | null; totalMarks: string | null; marksObtained: string | null;
  fatherName: string | null; fatherOccupation: string | null; fatherIncome: string | null;
  motherName: string | null; motherOccupation: string | null; motherIncome: string | null;
  wardNo: string | null; villageTown: string | null; tehsil: string | null;
  previousIllness: string | null; profilePicture: ProfilePicture | null;
  status: string; created_at: string | null; created_by: string | null;
  enrollments: Enrollment[]; is_enrolled: boolean; total_courses: number; is_registered: boolean;
}

interface CredentialResponse {
  email: string; temporary_password: string;
  institute_name?: string; branch_name?: string; courses?: string[];
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const StudentDetails: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);
  const [credentials, setCredentials] = useState<CredentialResponse | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const { profile } = useOperatorProfileStore();
  const toast = React.useRef<Toast>(null);
  const INSTITUTE_ID = profile?.institute_id;
  const BRANCH_ID = profile?.branch_id;

  useEffect(() => { if (studentId) fetchStudentDetails(); }, [studentId]);

  const fetchStudentDetails = async () => {
    if (!studentId) return;
    try {
      setLoading(true); setError(null);
      const response = await apiClient.get(`/v1/students/get-student/${studentId}`);
      if (response.data.success) setStudent(response.data.data);
      else setError('Failed to load student details');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load student details');
    } finally { setLoading(false); }
  };

  const handleGenerateCredentials = async () => {
    if (!studentId || !student) return;
    if (!student.is_enrolled || student.total_courses === 0) {
      toast.current?.show({ severity: 'error', summary: 'Enrollment Required', detail: 'Student must be enrolled in at least one course before generating credentials', life: 4000 });
      return;
    }
    if (!student.email) {
      toast.current?.show({ severity: 'error', summary: 'Missing Email', detail: 'Student must have an email address to generate credentials', life: 4000 });
      return;
    }
    if (!student.name || !student.dateOfBirth) {
      toast.current?.show({ severity: 'error', summary: 'Missing Information', detail: 'Student must have name and date of birth', life: 4000 });
      return;
    }
    try {
      setGeneratingCredentials(true);
      const response = await apiClient.post(`/v1/students/${studentId}/generate-credentials`);
      if (response.data.success) {
        const credData = response.data.data;
        setCredentials({ email: credData.email, temporary_password: credData.temporary_password, institute_name: credData.institute_name, branch_name: credData.branch_name, courses: credData.courses || [] });
        setEmailSent(response.data.email_sent === true);
        setShowCredentialsDialog(true);
        setStudent(prev => prev ? { ...prev, is_registered: true } : null);
        toast.current?.show({ severity: response.data.email_sent ? 'success' : 'warn', summary: response.data.email_sent ? 'Success' : 'Credentials Generated', detail: response.data.email_sent ? 'Credentials generated and welcome email sent!' : 'Credentials created but email could not be sent.', life: response.data.email_sent ? 4000 : 5000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to generate credentials', life: 4000 });
    } finally { setGeneratingCredentials(false); }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.current?.show({ severity: 'info', summary: 'Copied', detail: `${label} copied to clipboard`, life: 2000 });
  };

  const copyAllCredentials = () => {
    if (!credentials) return;
    const courses = credentials.courses?.length ? `\nEnrolled Courses: ${credentials.courses.join(', ')}` : '';
    const text = `Student Portal Access\n========================\nName: ${student?.name || 'N/A'}\nEmail/Username: ${credentials.email}\nTemporary Password: ${credentials.temporary_password}\nInstitute: ${credentials.institute_name || 'N/A'}\nBranch: ${credentials.branch_name || 'N/A'}${courses}\n\n⚠️ Please change your password immediately after first login.`.trim();
    navigator.clipboard.writeText(text);
    toast.current?.show({ severity: 'success', summary: 'Copied', detail: 'All credentials copied to clipboard', life: 2000 });
  };

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
      <div style={{ textAlign: 'center' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.95rem', fontWeight: '500' }}>Loading student details...</p>
      </div>
    </div>
  );

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !student) return (
    <div style={{ padding: '24px' }}>
      <Message severity="error" text={error || 'Student not found'} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />
      <button onClick={() => navigate('/students')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}>
        <i className="pi pi-arrow-left" /> Back to Students
      </button>
    </div>
  );

  // ── Main ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0' }}>
      <Toast ref={toast} />

      <ResetPasswordModal
        visible={showResetModal}
        onHide={() => setShowResetModal(false)}
        onSuccess={() => {
          setShowResetModal(false);
          toast.current?.show({ severity: 'success', summary: 'Password Reset', detail: `Password for ${student.name} has been reset successfully`, life: 4000 });
        }}
        profileId={student.student_id}
        role="student"
        name={student.name}
      />

      {/* ── Hero Banner — navy gradient ───────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
        borderRadius: '20px', padding: '32px', marginBottom: '24px',
        color: C.white, position: 'relative', overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(14,34,93,0.3)'
      }}>
        {/* Decorative orbs */}
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Back button */}
          <button
            onClick={() => navigate('/students')}
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: C.white, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', transition: 'all 0.2s ease' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <i className="pi pi-arrow-left" /> Back to Students
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
            {/* Left — avatar + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1, minWidth: '250px' }}>
              <Avatar
                image={student.profilePicture?.cdn_url || undefined}
                icon={!student.profilePicture ? 'pi pi-user' : undefined}
                size="xlarge" shape="circle"
                style={{ width: '100px', height: '100px', backgroundColor: student.profilePicture ? 'transparent' : C.goldDim, color: C.gold, border: `4px solid ${C.goldBorder}`, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
              />
              <div>
                {/* Badge pill */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                  <i className="pi pi-user" style={{ fontSize: '0.7rem' }} />
                  Student Profile
                </div>
                <h1 style={{ margin: '0 0 6px 0', fontSize: '1.75rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>{student.name}</h1>
                <p style={{ margin: '0 0 14px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>ID: {student.student_custom_id}</p>
                {/* Info chips */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {student.mobileNumber && (
                    <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '8px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="pi pi-phone" style={{ fontSize: '0.8rem', color: C.gold }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{student.mobileNumber}</span>
                    </div>
                  )}
                  {student.email && (
                    <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '8px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="pi pi-envelope" style={{ fontSize: '0.8rem', color: C.gold }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{student.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right — badges + dates */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
                {/* Status badge — gold/muted style */}
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px', ...(student.status === 'active' ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {student.status.toUpperCase()}
                </span>
                {student.is_enrolled && (
                  <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', background: C.goldDim, color: C.gold, border: `1px solid ${C.goldBorder}` }}>
                    {student.total_courses} COURSE{student.total_courses > 1 ? 'S' : ''}
                  </span>
                )}
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', ...(student.is_registered ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {student.is_registered ? 'REGISTERED' : 'NOT REGISTERED'}
                </span>
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' }}>DOB: {formatDate(student.dateOfBirth)}</p>
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>Created {formatDate(student.created_at)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Grid ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>

        {/* ── Left Column ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Not-registered warning */}
          {!student.is_registered && (
            <div style={{
              background: student.is_enrolled ? 'rgba(251,178,31,0.08)' : '#fff0f0',
              borderRadius: '16px', padding: '20px',
              boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
              border: student.is_enrolled ? `2px solid ${C.goldBorder}` : '2px solid #fca5a5',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem', color: student.is_enrolled ? '#92580a' : '#b91c1c' }} />
                  <div>
                    <p style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: '700', color: student.is_enrolled ? '#92580a' : '#b91c1c' }}>Account Not Registered</p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: student.is_enrolled ? '#92580a' : '#b91c1c' }}>
                      {student.is_enrolled ? 'Generate login credentials to allow student portal access. A welcome email will be sent automatically.' : 'Student must be enrolled in a course before generating credentials'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleGenerateCredentials}
                  disabled={!student.is_enrolled || generatingCredentials}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 20px',
                    background: student.is_enrolled ? C.gold : C.bg,
                    border: student.is_enrolled ? 'none' : `1px solid ${C.border}`,
                    borderRadius: '10px',
                    color: student.is_enrolled ? C.navy : C.muted,
                    fontSize: '0.85rem', fontWeight: '700',
                    cursor: student.is_enrolled ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap', opacity: student.is_enrolled ? 1 : 0.6,
                    boxShadow: student.is_enrolled ? '0 2px 8px rgba(251,178,31,0.35)' : 'none',
                    transition: 'all 0.2s ease', flexShrink: 0
                  }}
                  onMouseEnter={e => { if (student.is_enrolled) { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                  onMouseLeave={e => { if (student.is_enrolled) { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; } }}
                >
                  <i className={generatingCredentials ? 'pi pi-spin pi-spinner' : 'pi pi-key'} style={{ fontSize: '0.9rem' }} />
                  {generatingCredentials ? 'Generating...' : 'Generate Credentials'}
                </button>
              </div>
            </div>
          )}

          {/* Course Enrollments */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-graduation-cap" style={{ color: C.gold, fontSize: '1.1rem' }} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>Course Enrollments</h2>
                  <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: C.muted }}>Active course registrations</p>
                </div>
              </div>
              {/* Enroll button — gold */}
              <button
                onClick={() => setShowEnrollmentModal(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 18px', background: C.gold, border: 'none', borderRadius: '10px', color: C.navy, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(251,178,31,0.3)', transition: 'all 0.2s ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <i className="pi pi-plus" style={{ fontSize: '0.82rem' }} /> Enroll
              </button>
            </div>

            {student.enrollments && student.enrollments.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {student.enrollments.map((enrollment, index) => (
                  <div key={enrollment.enrollment_id} style={{ background: C.bg, padding: '16px', borderRadius: '12px', border: `1px solid ${C.border}`, position: 'relative', transition: 'all 0.2s ease' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = C.goldBorder; (e.currentTarget as HTMLDivElement).style.background = 'rgba(251,178,31,0.04)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = C.border; (e.currentTarget as HTMLDivElement).style.background = C.bg; }}
                  >
                    {/* Index badge */}
                    <div style={{ position: 'absolute', top: '12px', right: '12px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, color: C.gold, padding: '4px 10px', borderRadius: '12px', fontSize: '0.68rem', fontWeight: '700' }}>#{index + 1}</div>
                    <div style={{ paddingRight: '40px' }}>
                      {/* Enrollment No badge */}
                      {enrollment.enrollment_no && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '8px', padding: '4px 10px', marginBottom: '12px' }}>
                          <i className="pi pi-id-card" style={{ fontSize: '0.7rem', color: C.gold }} />
                          <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.navy, fontFamily: 'monospace', letterSpacing: '0.5px' }}>{enrollment.enrollment_no}</span>
                        </div>
                      )}
                      <p style={{ margin: '0 0 4px 0', fontSize: '0.68rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        <i className="pi pi-book" style={{ marginRight: '6px' }} />Course
                      </p>
                      <p style={{ margin: '0 0 2px 0', fontSize: '1rem', fontWeight: '700', color: C.navy }}>{enrollment.course_name || '-'}</p>
                      <p style={{ margin: '0 0 12px 0', fontSize: '0.8rem', color: C.sub }}>{enrollment.course_title || '-'}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <p style={{ margin: '0 0 4px 0', fontSize: '0.68rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Batch</p>
                          <p style={{ margin: '0 0 2px 0', fontSize: '0.85rem', fontWeight: '600', color: C.body }}>{enrollment.batch_name || '-'}</p>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: C.sub }}>{enrollment.batch_time || '-'}</p>
                        </div>
                        <div>
                          <p style={{ margin: '0 0 4px 0', fontSize: '0.68rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admitted</p>
                          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: C.body }}>{enrollment.date_of_admission || '-'}</p>
                        </div>
                      </div>
                      {enrollment.start_month && enrollment.start_year && (
                        <div style={{ marginTop: '12px', padding: '10px 14px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className="pi pi-calendar" style={{ fontSize: '0.8rem', color: C.gold }} />
                          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: C.white }}>
                            {enrollment.start_month} {enrollment.start_year} – {enrollment.end_month} {enrollment.end_year}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '40px 20px', textAlign: 'center', background: C.bg, borderRadius: '12px', border: `2px dashed ${C.border}` }}>
                <div style={{ width: '52px', height: '52px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <i className="pi pi-graduation-cap" style={{ fontSize: '1.5rem', color: C.navy, opacity: 0.25 }} />
                </div>
                <p style={{ margin: '0 0 6px 0', fontSize: '1rem', fontWeight: '700', color: C.navy }}>Not Enrolled</p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: C.muted }}>This student is not enrolled in any course yet.</p>
              </div>
            )}
          </div>

          <InfoSection title="Personal Information" subtitle="Student personal details" icon="pi-user" fields={[
            { label: 'Gender', value: student.gender ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1) : '-' },
            { label: 'Blood Group', value: student.bloodGroup || '-' },
            { label: 'Category', value: student.category || '-' },
            { label: 'Aadhaar Number', value: student.aadharNumber || '-' },
          ]} />

          <InfoSection title="Contact Information" subtitle="Student contact details" icon="pi-phone" fields={[
            { label: 'Mobile Number', value: student.mobileNumber || '-' },
            { label: 'WhatsApp Number', value: student.whatsappNumber || '-' },
            { label: 'Email', value: student.email || '-' },
            { label: "Father's Mobile", value: student.fatherMobile || '-' },
            { label: "Father's WhatsApp", value: student.fatherWhatsapp || '-' },
            { label: "Mother's Mobile", value: student.motherMobile || '-' },
            { label: "Mother's WhatsApp", value: student.motherWhatsapp || '-' },
          ]} />

          {/* Address */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <SectionHeader title="Address" subtitle="Residential information" icon="pi-map-marker" />
            <div style={{ background: C.bg, padding: '16px', borderRadius: '10px', marginBottom: '16px', border: `1px solid ${C.border}` }}>
              <p style={labelStyle}>Full Address</p>
              <p style={{ margin: 0, fontSize: '0.95rem', color: C.body, lineHeight: '1.5', fontWeight: '500' }}>
                {[student.wardNo && `Ward ${student.wardNo}`, student.villageTown, student.tehsil, student.district, student.state].filter(Boolean).join(', ') || '-'}
              </p>
              {student.pinCode && <p style={{ margin: '6px 0 0 0', fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Pincode: {student.pinCode}</p>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <InfoFieldItem label="Ward No" value={student.wardNo || '-'} />
              <InfoFieldItem label="Village/Town" value={student.villageTown || '-'} />
              <InfoFieldItem label="Tehsil" value={student.tehsil || '-'} />
            </div>
          </div>

          {/* Education & Family */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <SectionHeader title="Education & Family" subtitle="Academic and family details" icon="pi-book" />
            <div style={{ background: C.bg, padding: '16px', borderRadius: '10px', marginBottom: '16px', border: `1px solid ${C.border}` }}>
              <p style={labelStyle}>Academic Details</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <InfoFieldItem label="Previous Class" value={student.previousClass || '-'} />
                <InfoFieldItem label="Year of Passing" value={student.yearOfPassing || '-'} />
                <InfoFieldItem label="School/Board" value={student.schoolBoard || '-'} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <InfoFieldItem label="Total Marks" value={student.totalMarks || '-'} />
                <InfoFieldItem label="Marks Obtained" value={student.marksObtained || '-'} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {[
                { title: "Father's Details", fields: [['Name', student.fatherName], ['Occupation', student.fatherOccupation], ['Income', student.fatherIncome]] as [string, string | null][] },
                { title: "Mother's Details", fields: [['Name', student.motherName], ['Occupation', student.motherOccupation], ['Income', student.motherIncome]] as [string, string | null][] },
              ].map(parent => (
                <div key={parent.title} style={{ background: C.bg, padding: '14px', borderRadius: '10px', border: `1px solid ${C.border}` }}>
                  <p style={labelStyle}>{parent.title}</p>
                  {parent.fields.map(([label, value]) => (
                    <InfoFieldItem key={label} label={label} value={value || '-'} compact />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Medical */}
          {student.previousIllness && (
            <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <SectionHeader title="Medical Information" subtitle="Health history" icon="pi-info-circle" />
              <InfoFieldItem label="Previous Illness" value={student.previousIllness} />
            </div>
          )}
        </div>

        {/* ── Right Column ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Quick Info */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-id-card" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Quick Info</h3>
            </div>

            {/* Avatar card — navy gradient */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '12px', marginBottom: '16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: C.goldDim, borderRadius: '50%', filter: 'blur(20px)', pointerEvents: 'none' }} />
              <Avatar
                image={student.profilePicture?.cdn_url || undefined}
                icon={!student.profilePicture ? 'pi pi-user' : undefined}
                size="xlarge" shape="circle"
                style={{ width: '80px', height: '80px', backgroundColor: student.profilePicture ? 'transparent' : C.goldDim, color: C.gold, marginBottom: '12px', border: `3px solid ${C.goldBorder}` }}
              />
              <p style={{ margin: '0 0 4px 0', fontSize: '1.1rem', fontWeight: '700', color: C.white, textAlign: 'center', position: 'relative', zIndex: 1 }}>{student.name}</p>
              <p style={{ margin: '0 0 12px 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', position: 'relative', zIndex: 1 }}>Student Profile</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', ...(student.status === 'active' ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {student.status.toUpperCase()}
                </span>
                <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', ...(student.is_registered ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {student.is_registered ? 'REGISTERED' : 'NOT REGISTERED'}
                </span>
              </div>
            </div>

            <QuickInfoRow label="Student ID" value={student.student_custom_id} mono />
            <QuickInfoRow label="Date of Birth" value={formatDate(student.dateOfBirth)} />
            {student.gender && <QuickInfoRow label="Gender" value={student.gender.charAt(0).toUpperCase() + student.gender.slice(1)} />}
            {student.category && <QuickInfoRow label="Category" value={student.category} />}
          </div>

          {/* Enrollment Stats */}
          {student.is_enrolled && (
            <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-chart-bar" style={{ color: C.gold, fontSize: '1rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Enrollment Stats</h3>
              </div>
              <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, padding: '20px', borderRadius: '12px', textAlign: 'center', marginBottom: '16px' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '2.5rem', fontWeight: '800', color: C.gold }}>{student.total_courses}</p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Course{student.total_courses > 1 ? 's' : ''}</p>
              </div>

              {student.enrollments.some(e => e.enrollment_no) && (
                <div>
                  <p style={labelStyle}>Enrollment Numbers</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {student.enrollments.filter(e => e.enrollment_no).map(e => (
                      <div key={e.enrollment_id} style={{ background: C.bg, borderRadius: '8px', padding: '10px 12px', border: `1px solid ${C.border}` }}>
                        <p style={{ margin: '0 0 3px 0', fontSize: '0.65rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{e.course_name || 'Course'}</p>
                        <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '700', color: C.navy, fontFamily: 'monospace', letterSpacing: '0.3px' }}>{e.enrollment_no}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Password Management */}
          {student.is_registered && (
            <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-lock" style={{ color: C.gold, fontSize: '0.9rem' }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Password Management</p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: C.muted }}>Operator controls</p>
                </div>
              </div>
              <p style={{ margin: '0 0 14px 0', fontSize: '0.78rem', color: C.sub, lineHeight: '1.5' }}>
                Reset this student's login password if they've forgotten it or need access restored.
              </p>
              <button
                onClick={() => setShowResetModal(true)}
                style={{ width: '100%', padding: '11px 16px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(14,34,93,0.2)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)'; }}
              >
                <i className="pi pi-refresh" style={{ fontSize: '0.82rem' }} />
                Reset Password
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Action Buttons Bar ──────────────────────────────────────────────── */}
      <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`, display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* Primary — Edit Student (gold) */}
        <button
          onClick={() => navigate(`/students/edit/${studentId}`)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: C.gold, border: 'none', borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(251,178,31,0.3)', transition: 'all 0.2s ease' }}
          onMouseEnter={e => { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <i className="pi pi-pencil" style={{ fontSize: '0.85rem' }} /> Edit Student
        </button>
        {/* Secondary — navy outlined style */}
        {[
          { label: 'Enroll in Course', icon: 'pi-graduation-cap', onClick: () => setShowEnrollmentModal(true) },
          { label: 'View Attendance', icon: 'pi-calendar', onClick: () => {} },
          { label: 'View Performance', icon: 'pi-chart-line', onClick: () => {} },
        ].map(action => (
          <button
            key={action.label}
            onClick={action.onClick}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; }}
          >
            <i className={`pi ${action.icon}`} style={{ fontSize: '0.85rem' }} /> {action.label}
          </button>
        ))}
        {!student.is_registered && (
          <button
            onClick={handleGenerateCredentials}
            disabled={!student.is_enrolled || generatingCredentials}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'transparent', border: `1.5px solid ${student.is_enrolled ? '#92580a' : C.border}`, borderRadius: '10px', color: student.is_enrolled ? '#92580a' : C.muted, fontSize: '0.85rem', fontWeight: '600', cursor: student.is_enrolled ? 'pointer' : 'not-allowed', transition: 'all 0.2s ease', opacity: student.is_enrolled ? 1 : 0.6 }}
          >
            <i className="pi pi-key" style={{ fontSize: '0.85rem' }} /> Generate Credentials
          </button>
        )}
        {student.is_registered && (
          <button
            onClick={() => setShowResetModal(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; }}
          >
            <i className="pi pi-lock" style={{ fontSize: '0.85rem' }} /> Reset Password
          </button>
        )}
      </div>

      {/* ── Enrollment Modal ─────────────────────────────────────────────────── */}
      {student && (
        <StudentEnrollmentModal
          visible={showEnrollmentModal}
          studentId={student.student_id}
          studentName={student.name}
          instituteId={INSTITUTE_ID}
          branchId={BRANCH_ID}
          existingEnrollments={student.enrollments.map(e => ({ course_id: e.course_id || '', batch_id: e.batch_id || '', date_of_admission: e.date_of_admission || '' }))}
          onHide={() => setShowEnrollmentModal(false)}
          onSuccess={() => { fetchStudentDetails(); setShowEnrollmentModal(false); }}
        />
      )}

      {/* ── Credentials Dialog ───────────────────────────────────────────────── */}
      <Dialog
        visible={showCredentialsDialog}
        style={{ width: '560px' }}
        onHide={() => { setShowCredentialsDialog(false); setCredentials(null); setEmailSent(false); }}
        pt={{
          root: { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
          header: { style: { display: 'none' } },
          content: { style: { padding: '0' } },
          footer: { style: { display: 'none' } }
        }}
      >
        {credentials && (
          <div>
            {/* Dialog header — navy gradient */}
            <div style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`, padding: '24px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '100px', height: '100px', background: C.goldDim, borderRadius: '50%', filter: 'blur(20px)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '44px', height: '44px', background: C.gold, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="pi pi-check-circle" style={{ fontSize: '1.3rem', color: C.navy }} />
                </div>
                <div>
                  <h3 style={{ margin: '0 0 3px 0', fontSize: '1.2rem', fontWeight: '700', color: C.white }}>Credentials Generated</h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{emailSent ? 'Welcome email sent successfully' : 'Please share credentials manually'}</p>
                </div>
              </div>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Email status banner */}
              <div style={{ background: emailSent ? '#f0fdf4' : 'rgba(251,178,31,0.08)', padding: '14px 16px', borderRadius: '12px', marginBottom: '20px', border: emailSent ? '1px solid #86efac' : `1px solid ${C.goldBorder}`, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <i className={emailSent ? 'pi pi-check-circle' : 'pi pi-info-circle'} style={{ fontSize: '1.1rem', color: emailSent ? '#15803d' : '#92580a', marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '0.88rem', fontWeight: '700', color: emailSent ? '#15803d' : '#92580a' }}>
                    {emailSent ? 'Welcome Email Sent Successfully' : 'Manual Sharing Required'}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: '1.5', color: emailSent ? '#166534' : '#78350f' }}>
                    {emailSent ? `A welcome email with login credentials has been sent to ${credentials.email}.` : 'The welcome email could not be sent. Please share credentials manually.'}
                  </p>
                </div>
              </div>

              {/* Student context */}
              {(credentials.institute_name || credentials.branch_name || credentials.courses?.length) && (
                <div style={{ background: C.bg, padding: '14px 16px', borderRadius: '12px', marginBottom: '20px', border: `1px solid ${C.border}` }}>
                  <p style={labelStyle}>Student Details</p>
                  {credentials.institute_name && <p style={{ margin: '0 0 6px 0', fontSize: '0.82rem', color: C.body }}><span style={{ color: C.muted, fontWeight: '600' }}>Institute: </span>{credentials.institute_name}</p>}
                  {credentials.branch_name && <p style={{ margin: '0 0 6px 0', fontSize: '0.82rem', color: C.body }}><span style={{ color: C.muted, fontWeight: '600' }}>Branch: </span>{credentials.branch_name}</p>}
                  {credentials.courses?.length && <p style={{ margin: 0, fontSize: '0.82rem', color: C.body }}><span style={{ color: C.muted, fontWeight: '600' }}>Courses: </span>{credentials.courses.join(', ')}</p>}
                </div>
              )}

              <CredentialRow label="Email / Username" value={credentials.email} onCopy={() => copyToClipboard(credentials.email, 'Email')} />
              <CredentialRow label="Temporary Password" value={credentials.temporary_password} onCopy={() => copyToClipboard(credentials.temporary_password, 'Password')} highlight />

              {/* Security notice — gold tinted */}
              <div style={{ background: 'rgba(251,178,31,0.08)', border: `1px solid ${C.goldBorder}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <i className="pi pi-shield" style={{ fontSize: '1rem', color: '#92580a', marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: '#92580a' }}>Important Security Notice</p>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#78350f', lineHeight: '1.5' }}>Student must change this temporary password immediately after first login.</p>
                </div>
              </div>

              {/* Dialog footer actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {emailSent
                    ? <><i className="pi pi-check-circle" style={{ color: '#15803d', fontSize: '0.9rem' }} /><span style={{ fontSize: '0.82rem', color: '#15803d', fontWeight: '600' }}>Email sent</span></>
                    : <><i className="pi pi-exclamation-triangle" style={{ color: '#92580a', fontSize: '0.9rem' }} /><span style={{ fontSize: '0.82rem', color: '#92580a', fontWeight: '600' }}>Email not sent</span></>}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={copyAllCredentials}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <i className="pi pi-copy" /> Copy All
                  </button>
                  <button
                    onClick={() => { setShowCredentialsDialog(false); setCredentials(null); setEmailSent(false); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.2)', transition: 'all 0.2s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)'; }}
                  >
                    <i className="pi pi-times" /> Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

// ── Shared label style ────────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  margin: '0 0 8px 0', fontSize: '0.68rem', color: '#9ca3af',
  fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px'
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string; subtitle: string; icon: string }> = ({ title, subtitle, icon }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
    <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '1.1rem' }} />
    </div>
    <div>
      <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#0e225d' }}>{title}</h2>
      <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>{subtitle}</p>
    </div>
  </div>
);

interface InfoSectionProps {
  title: string; subtitle: string; icon: string;
  fields: Array<{ label: string; value: string | null }>;
}
const InfoSection: React.FC<InfoSectionProps> = ({ title, subtitle, icon, fields }) => (
  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB' }}>
    <SectionHeader title={title} subtitle={subtitle} icon={icon} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
      {fields.map((f, i) => <InfoFieldItem key={i} label={f.label} value={f.value} />)}
    </div>
  </div>
);

const InfoFieldItem: React.FC<{ label: string; value: string | null; compact?: boolean }> = ({ label, value, compact }) => (
  <div style={{ marginBottom: compact ? '8px' : '0' }}>
    <p style={{ margin: '0 0 5px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <p style={{ margin: 0, fontSize: compact ? '0.8rem' : '0.9rem', fontWeight: '600', color: '#1f2937' }}>{value || '-'}</p>
  </div>
);

const QuickInfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div style={{ marginBottom: '12px' }}>
    <p style={{ margin: '0 0 5px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <p style={{ margin: 0, fontSize: mono ? '0.8rem' : '0.85rem', fontWeight: '600', color: mono ? '#0e225d' : '#1f2937', fontFamily: mono ? 'monospace' : undefined, background: mono ? '#F5F7FA' : undefined, padding: mono ? '8px 10px' : undefined, borderRadius: mono ? '8px' : undefined, border: mono ? '1px solid #E5E7EB' : undefined, wordBreak: 'break-all' }}>{value}</p>
  </div>
);

const CredentialRow: React.FC<{ label: string; value: string; onCopy: () => void; highlight?: boolean }> = ({ label, value, onCopy, highlight }) => (
  <div style={{ marginBottom: '14px' }}>
    <p style={{ margin: '0 0 7px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: highlight ? 'rgba(251,178,31,0.08)' : '#F5F7FA', padding: '12px 14px', borderRadius: '10px', border: highlight ? '1px solid rgba(251,178,31,0.35)' : '1px solid #E5E7EB' }}>
      <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: highlight ? '700' : '600', color: highlight ? '#92580a' : '#0e225d', flex: 1, fontFamily: 'monospace', letterSpacing: highlight ? '1px' : '0.3px' }}>{value}</p>
      <button onClick={onCopy} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', color: highlight ? '#92580a' : '#0e225d', transition: 'background 0.2s' }} title="Copy"
        onMouseEnter={e => e.currentTarget.style.background = highlight ? 'rgba(251,178,31,0.15)' : 'rgba(14,34,93,0.08)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <i className="pi pi-copy" style={{ fontSize: '0.9rem' }} />
      </button>
    </div>
  </div>
);

export default StudentDetails;